/**
 * デバイス登録（プロビジョニング）ページ - melta UI
 * カード型レイアウト: 左に単体登録、右に一括登録
 *
 * 機能:
 * - 証明書一式 + config.json を ZIP でダウンロード
 * - スクリプトは解凍ディレクトリで直接実行（パス指定不要）
 * - SCP コマンドを表示
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { provisionApi, thingTypeApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Input, Select, Alert, Badge } from '../components/ui';
import { Textarea } from '../components/ui/Input';
import { setupScript, connectTestScript, readmeText } from '../utils/deviceScripts';

export default function Provision() {
  const navigate = useNavigate();

  const [thingTypes, setThingTypes] = useState([]);
  const [formData, setFormData] = useState({
    thingName: '',
    thingTypeName: '',
    attributes: [{ key: '', value: '' }],
  });
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [bulkDevices, setBulkDevices] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  const thingNameRef = useRef(null);

  useEffect(() => {
    loadThingTypes();
  }, []);

  useEffect(() => {
    setTimeout(() => thingNameRef.current?.focus(), 100);
  }, []);

  const loadThingTypes = async () => {
    try {
      const data = await thingTypeApi.list();
      setThingTypes(data.thingTypes || []);
    } catch (err) {
      console.warn('ThingTypes取得失敗:', err.message);
    }
  };

  /**
   * config.json を生成
   * パスはスクリプト実行ディレクトリからの相対パス（./）を使用
   */
  const generateConfigJson = (thingName, endpoint, region) => {
    return JSON.stringify({
      thingName,
      endpoint,
      region,
      certPath: `./${thingName}.pem.crt`,
      keyPath: `./${thingName}.pem.key`,
      caPath: `./AmazonRootCA1.pem`,
    }, null, 2);
  };

  /**
   * ZIP ファイルを生成してダウンロード
   * 証明書 + スクリプト + README を含む
   */
  const downloadCertificateZip = async (deviceResult) => {
    setIsDownloading(true);
    try {
      const { thingName, certificatePem, privateKey, iotEndpoint, region } = deviceResult;

      // Amazon Root CA証明書をAPIから取得
      const rootCaResponse = await provisionApi.getRootCa();
      const amazonRootCa = rootCaResponse.certificate;

      const zip = new JSZip();

      // スクリプト用の設定オブジェクト
      const scriptConfig = {
        thingName,
        endpoint: iotEndpoint,
        region,
      };

      // 証明書ファイル
      zip.file(`${thingName}.pem.crt`, certificatePem);
      zip.file(`${thingName}.pem.key`, privateKey);
      zip.file('AmazonRootCA1.pem', amazonRootCa);

      // config.json（相対パス使用）
      const configJson = generateConfigJson(thingName, iotEndpoint, region);
      zip.file('config.json', configJson);

      // スクリプト類
      zip.file('setup.sh', setupScript(scriptConfig));
      zip.file('connect-test.sh', connectTestScript(scriptConfig));
      zip.file('README.txt', readmeText(scriptConfig));

      // ZIP 生成 & ダウンロード
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${thingName}-certs.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ZIP作成エラー:', err);
      alert('ZIPファイルの作成に失敗しました');
    } finally {
      setIsDownloading(false);
    }
  };

  // 単体登録
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setIsSubmitting(true);

    try {
      const attributes = {};
      formData.attributes.forEach(({ key, value }) => {
        if (key.trim()) attributes[key.trim()] = value.trim();
      });

      const body = {
        thingName: formData.thingName.trim(),
        attributes,
      };
      if (formData.thingTypeName) body.thingTypeName = formData.thingTypeName;

      const data = await provisionApi.create(body);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addAttributeRow = () =>
    setFormData({ ...formData, attributes: [...formData.attributes, { key: '', value: '' }] });

  const removeAttributeRow = (index) =>
    setFormData({ ...formData, attributes: formData.attributes.filter((_, i) => i !== index) });

  const updateAttribute = (index, field, value) => {
    const updated = [...formData.attributes];
    updated[index][field] = value;
    setFormData({ ...formData, attributes: updated });
  };

  const downloadFile = (filename, content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({ thingName: '', thingTypeName: '', attributes: [{ key: '', value: '' }] });
    setResult(null);
    setError(null);
    setTimeout(() => thingNameRef.current?.focus(), 50);
  };

  const goToDeviceList = () => navigate('/devices');
  const goToDeviceDetail = () => navigate(`/devices/${result.thingName}`);

  /**
   * SCP コマンドをクリップボードにコピー
   */
  const copyScpCommand = (thingName) => {
    const cmd = `scp ${thingName}-certs.zip pi@<DEVICE_IP>:~/`;
    navigator.clipboard.writeText(cmd);
    alert('SCPコマンドをコピーしました');
  };

  // 一括登録
  const handleBulkSubmit = async () => {
    setBulkError(null);
    setBulkResult(null);
    setIsBulkSubmitting(true);

    try {
      const lines = bulkDevices.trim().split('\n').filter(l => l.trim());
      const devices = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        const thingName = parts[0];
        const thingTypeName = parts[1] || undefined;
        const attributes = {};
        parts.slice(2).forEach(attr => {
          const [k, v] = attr.split('=');
          if (k) attributes[k.trim()] = (v || '').trim();
        });
        return { thingName, thingTypeName, attributes };
      });

      const data = await provisionApi.bulk(devices);
      setBulkResult(data);
    } catch (err) {
      setBulkError(err.message);
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  /**
   * 一括登録結果から全デバイスのZIPをダウンロード
   */
  const downloadAllBulkZips = async () => {
    const successDevices = bulkResult.results.filter(r => r.status === 'success');
    for (const device of successDevices) {
      await downloadCertificateZip(device);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">新規機体登録</p>

      {/* 説明セクション */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-100 rounded-full p-2 mt-0.5">
            <Icon name="info" className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-medium text-blue-900 mb-2">この画面について</p>
            <p className="text-blue-700 mb-3">
              AWS IoT Core に新しい機体（Thing）を登録し、接続に必要な証明書一式を取得します。
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {/* 登録の流れ */}
              <div>
                <p className="font-medium text-blue-800 mb-1">登録の流れ</p>
                <ol className="text-blue-600 space-y-0.5 list-decimal list-inside">
                  <li>機体名を入力して「登録」</li>
                  <li>証明書ZIPをダウンロード</li>
                  <li>ZIPをデバイスに転送</li>
                  <li>スクリプトのセットアップ実行（パーミッション設定）</li>
                </ol>
              </div>

              {/* 必要な情報 */}
              <div>
                <p className="font-medium text-blue-800 mb-1">入力項目</p>
                <ul className="text-blue-600 space-y-0.5">
                  <li><span className="text-red-500">*</span> 機体名（必須）- 一意の識別子</li>
                  <li>機体タイプ（任意）- 分類用</li>
                  <li>属性（任意）- メタデータ</li>
                </ul>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-blue-200 space-y-1">
              <p className="text-amber-700 text-xs">
                <Icon name="alertTriangle" className="w-3 h-3 inline mr-1" />
                <strong>重要:</strong> 秘密鍵は登録時に1回のみダウンロード可能です。紛失した場合は証明書のローテーションが必要になります。
              </p>
              <p className="text-blue-600 text-xs">
                <Icon name="fileText" className="w-3 h-3 inline mr-1" />
                詳細な手順やトラブルシューティングは、ZIPに含まれる <strong>README.txt</strong> を参照してください。
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* 左: 単体登録 */}
        <div className="space-y-4">
          {!result ? (
            <Card title="単体登録">
              {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  ref={thingNameRef}
                  label="機体名"
                  required
                  value={formData.thingName}
                  onChange={(e) => setFormData({ ...formData, thingName: e.target.value })}
                  placeholder="例: drone-001"
                  pattern="[a-zA-Z0-9_\-]+"
                />
                <p className="text-xs text-slate-500 -mt-2">
                  英数字・ハイフン・アンダースコアのみ
                </p>

                <Select
                  label="機体タイプ"
                  value={formData.thingTypeName}
                  onChange={(e) => setFormData({ ...formData, thingTypeName: e.target.value })}
                  options={[
                    { value: '', label: '-- 選択なし --' },
                    ...thingTypes.map(t => ({ value: t.thingTypeName, label: t.thingTypeName }))
                  ]}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    属性 (Attributes)
                  </label>
                  <div className="space-y-2">
                    {formData.attributes.map((attr, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Key"
                          value={attr.key}
                          onChange={(e) => updateAttribute(i, 'key', e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        />
                        <input
                          type="text"
                          placeholder="Value"
                          value={attr.value}
                          onChange={(e) => updateAttribute(i, 'value', e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        />
                        {formData.attributes.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            iconOnly
                            onClick={() => removeAttributeRow(i)}
                            aria-label="削除"
                          >
                            <Icon name="x" className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={addAttributeRow}
                  >
                    <Icon name="plus" className="w-4 h-4" />
                    属性を追加
                  </Button>
                </div>

                <div className="pt-2">
                  <Button type="submit" size="sm" loading={isSubmitting}>
                    登録
                  </Button>
                </div>
              </form>
            </Card>
          ) : (
            <Card title="登録完了">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="checkCircle" className="w-6 h-6 text-emerald-500" />
                </div>
                <p className="text-slate-700">
                  <span className="font-medium">{result.thingName}</span> を登録しました
                </p>
              </div>

              {result.steps && (
                <div className="space-y-1 mb-4">
                  {result.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {step.status === 'success' ? (
                        <Icon name="checkCircle" className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Icon name="xCircle" className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-slate-600">{step.step}</span>
                    </div>
                  ))}
                </div>
              )}

              <Alert variant="warning" className="mb-4 text-sm">
                秘密鍵は今回のみダウンロード可能です
              </Alert>

              {/* ZIP ダウンロード（推奨） */}
              <div className="mb-4">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => downloadCertificateZip(result)}
                  loading={isDownloading}
                  className="w-full"
                >
                  <Icon name="download" className="w-4 h-4" />
                  証明書一式をZIPでダウンロード
                </Button>
                <p className="text-xs text-slate-500 mt-1 text-center">
                  証明書・秘密鍵・CA・config.json が含まれます
                </p>
              </div>

              {/* 個別ダウンロード */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => downloadFile(`${result.thingName}.pem.crt`, result.certificatePem)}
                >
                  <Icon name="download" className="w-4 h-4" />
                  証明書
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => downloadFile(`${result.thingName}.pem.key`, result.privateKey)}
                >
                  <Icon name="key" className="w-4 h-4" />
                  秘密鍵
                </Button>
              </div>

              {/* ZIPファイルの内容 */}
              <div className="bg-blue-50 rounded-lg p-3 mb-4 text-xs">
                <p className="font-medium text-blue-700 mb-2">ZIPファイルの内容:</p>
                <div className="bg-white rounded p-2 mb-2 font-mono text-slate-700">
                  <div className="text-blue-600 mb-1">{result.thingName}-certs.zip</div>
                  <div className="pl-3 space-y-0.5">
                    <div>├── {result.thingName}.pem.crt <span className="text-slate-400">（デバイス証明書）</span></div>
                    <div>├── {result.thingName}.pem.key <span className="text-slate-400">（秘密鍵）</span></div>
                    <div>├── AmazonRootCA1.pem <span className="text-slate-400">（ルートCA証明書）</span></div>
                    <div>├── config.json <span className="text-slate-400">（接続設定）</span></div>
                    <div>├── setup.sh <span className="text-slate-400">（セットアップスクリプト）</span></div>
                    <div>├── connect-test.sh <span className="text-slate-400">（接続テスト）</span></div>
                    <div>└── README.txt <span className="text-slate-400">（使用方法）</span></div>
                  </div>
                </div>
                <p className="text-blue-600">※ 追加パッケージ不要（curl のみ使用）</p>
              </div>

              {/* デバイスでの使用手順 */}
              <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <p className="font-medium text-slate-700 text-sm">デバイスでの使用手順</p>
                </div>
                <div className="p-3 space-y-3 text-xs">
                  {/* Step 1 */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-slate-700 text-white px-1.5 py-0.5 rounded text-xs font-medium">1</span>
                      <span className="font-medium text-slate-700">PCからデバイスへZIPを送信</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyScpCommand(result.thingName)}
                        className="text-slate-400 hover:text-slate-700 ml-auto"
                      >
                        <Icon name="copy" className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <code className="text-emerald-400">
                        scp {result.thingName}-certs.zip pi@&lt;DEVICE_IP&gt;:~/
                      </code>
                    </div>
                    <p className="text-slate-500 mt-1 pl-1">
                      → <code className="bg-slate-100 px-1 rounded">pi</code> はデバイスのユーザー名、<code className="bg-slate-100 px-1 rounded">:~/</code> は保存先ディレクトリ（ホーム）
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-slate-700 text-white px-1.5 py-0.5 rounded text-xs font-medium">2</span>
                      <span className="font-medium text-slate-700">デバイス側でZIPを解凍</span>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <pre className="text-emerald-400 whitespace-pre-wrap">{`unzip ~/${result.thingName}-certs.zip -d ~/${result.thingName}
cd ~/${result.thingName}`}</pre>
                    </div>
                    <p className="text-slate-500 mt-1 pl-1">
                      → 解凍先フォルダがそのまま証明書の格納場所になります
                    </p>
                  </div>

                  {/* Step 3 */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-slate-700 text-white px-1.5 py-0.5 rounded text-xs font-medium">3</span>
                      <span className="font-medium text-slate-700">スクリプトのセットアップ実行（パーミッション設定）</span>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <pre className="text-emerald-400 whitespace-pre-wrap">{`chmod +x setup.sh connect-test.sh
./setup.sh`}</pre>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-slate-700 text-white px-1.5 py-0.5 rounded text-xs font-medium">4</span>
                      <span className="font-medium text-slate-700">接続テスト</span>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <code className="text-emerald-400">./connect-test.sh</code>
                    </div>
                    <p className="text-slate-500 mt-1 pl-1">
                      → <code className="bg-slate-100 px-1 rounded">all</code> オプションで全テスト実行可
                    </p>
                  </div>
                </div>
              </div>

              {/* 注意事項 */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs">
                <p className="font-medium text-amber-700 mb-1">注意事項:</p>
                <ul className="text-amber-600 space-y-1">
                  <li>・解凍したフォルダ内で直接スクリプトを実行してください</li>
                  <li>・別の場所に移動する場合はフォルダごと移動してください</li>
                  <li>・秘密鍵（.pem.key）は安全に保管し、他者と共有しないでください</li>
                  <li>・<code className="bg-amber-100 px-1 rounded">sudo</code> で解凍した場合、所有者が root になります。
                    <code className="bg-amber-100 px-1 rounded ml-1">sudo chown -R $(whoami) .</code> で変更してください</li>
                </ul>
                <p className="text-amber-700 mt-2 pt-2 border-t border-amber-200">
                  <Icon name="fileText" className="w-3 h-3 inline mr-1" />
                  トラブルシューティングや詳細は <strong>README.txt</strong> を参照してください
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" onClick={resetForm}>
                  別の機体を登録
                </Button>
                <Button variant="secondary" size="sm" onClick={goToDeviceList}>
                  一覧へ
                </Button>
                <Button size="sm" onClick={goToDeviceDetail}>
                  詳細
                </Button>
              </div>
            </Card>
          )}

          {/* 説明（登録前/後で切り替え） */}
          {!result ? (
            <Alert variant="info">
              <div className="space-y-1 text-sm">
                <p className="font-medium">登録時に自動実行:</p>
                <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                  <li>IoT Core に機体（Thing）を作成</li>
                  <li>X.509 証明書 & 秘密鍵を生成</li>
                  <li>機体ポリシーをアタッチ</li>
                </ul>
              </div>
            </Alert>
          ) : (
            <Alert variant="info">
              <div className="space-y-2 text-sm">
                <p className="font-medium">config.json の内容:</p>
                <div className="bg-white/50 rounded p-2 font-mono text-xs">
                  <div><span className="text-slate-500">thingName:</span> {result.thingName}</div>
                  <div><span className="text-slate-500">endpoint:</span> {result.iotEndpoint}</div>
                  <div><span className="text-slate-500">region:</span> {result.region}</div>
                </div>
                <p className="text-slate-500 text-xs">
                  アプリケーションからはこの config.json を読み込んで接続できます
                </p>
              </div>
            </Alert>
          )}
        </div>

        {/* 右: 一括登録 */}
        <div className="space-y-4">
          {!bulkResult ? (
            <Card title="一括登録 (CSV)">
              {bulkError && <Alert variant="error" onClose={() => setBulkError(null)} className="mb-4">{bulkError}</Alert>}

              <Textarea
                label="CSV形式で入力"
                value={bulkDevices}
                onChange={(e) => setBulkDevices(e.target.value)}
                rows={8}
                placeholder={`thingName,thingType,key1=value1\ndrone-001,Drone,manufacturer=DJI\ndrone-002,Drone`}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-2 mb-4">
                形式: thingName,thingType,属性1=値1（1行1デバイス、最大50台）
              </p>

              <Button
                size="sm"
                onClick={handleBulkSubmit}
                disabled={!bulkDevices.trim()}
                loading={isBulkSubmitting}
              >
                一括登録
              </Button>
            </Card>
          ) : (
            <Card title="一括登録結果">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="success">成功: {bulkResult.successCount}</Badge>
                <Badge variant="danger">失敗: {bulkResult.failCount}</Badge>
                <span className="text-sm text-slate-500">/ 全 {bulkResult.totalRequested} 件</span>
              </div>

              {/* 全デバイスZIPダウンロード */}
              {bulkResult.successCount > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={downloadAllBulkZips}
                  className="mb-4 w-full"
                >
                  <Icon name="download" className="w-4 h-4" />
                  成功分を全てZIPダウンロード
                </Button>
              )}

              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                {bulkResult.results?.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                      r.status === 'success' ? 'bg-emerald-50' : 'bg-red-50'
                    }`}
                  >
                    {r.status === 'success' ? (
                      <Icon name="checkCircle" className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <Icon name="xCircle" className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span className="font-medium text-slate-900 flex-1 truncate">{r.thingName}</span>
                    {r.status === 'success' && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadCertificateZip(r)}
                          title="ZIP"
                        >
                          <Icon name="download" className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    {r.error && <span className="text-xs text-red-600 truncate">{r.error}</span>}
                  </div>
                ))}
              </div>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setBulkResult(null); setBulkDevices(''); }}
              >
                新規一括登録
              </Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
